/**
 * Performance Metric Service – QMS Phase 3
 * CRUD for performance_metrics linked to Standards (processes).
 * PRD §5.1 Requirement 9.1
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

const COLLECTION = 'performance_metrics';

const METRIC_TYPES = ['compliance', 'operational', 'clinical', 'risk'];
const UNITS = ['percentage', 'count', 'rate_per_1000'];
const FREQUENCIES = ['daily', 'weekly', 'monthly', 'quarterly', 'annual'];
const TRENDS = ['improving', 'stable', 'declining'];
const DATA_SOURCES = ['manual', 'system'];

function omitUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export class PerformanceMetricService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  buildPayload(data, userId, now) {
    const base = {
      organizationId: data.organizationId ?? null,
      processId: data.processId ?? null,
      processCode: data.processCode ?? null,
      processName: data.processName ?? null,
      name: data.name || '',
      metricType: data.metricType || 'operational',
      targetValue: data.targetValue ?? null,
      targetOperator: data.targetOperator || '>=',
      unit: data.unit || 'percentage',
      measurementFrequency: data.measurementFrequency || 'monthly',
      dataSource: data.dataSource || 'manual',
      currentValue: data.currentValue ?? null,
      lastMeasuredAt: data.lastMeasuredAt ? Timestamp.fromDate(new Date(data.lastMeasuredAt)) : null,
      trend: data.trend || 'stable',
      previousValue: data.previousValue ?? null,
      notes: data.notes || '',
      isActive: data.isActive !== false,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    return omitUndefined(base);
  }

  isBelowTarget(metric) {
    const { currentValue, targetValue, targetOperator } = metric;
    if (currentValue == null || targetValue == null) return false;
    switch (targetOperator) {
      case '>=':
        return currentValue < targetValue;
      case '<=':
        return currentValue > targetValue;
      case '>':
        return currentValue <= targetValue;
      case '<':
        return currentValue >= targetValue;
      case '==':
        return currentValue !== targetValue;
      default:
        return currentValue < targetValue;
    }
  }

  async create(data, userId) {
    const now = Timestamp.now();
    const payload = this.buildPayload(data, userId, now);
    const ref = await addDoc(this.collectionRef, payload);
    try {
      auditService.logCreate('performance_metrics', ref.id, data.name || 'Performance metric', {
        processId: data.processId,
        metricType: data.metricType
      });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
    return ref.id;
  }

  async get(metricId) {
    const ref = doc(this.collectionRef, metricId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const d = snap.data();
    return {
      id: snap.id,
      ...d,
      lastMeasuredAt: d.lastMeasuredAt?.toDate?.() ?? d.lastMeasuredAt
    };
  }

  async getByOrganization(organizationId, options = {}) {
    const { processId, belowTargetOnly = false, limit = 200 } = options;
    const org = organizationId ?? null;
    const constraints = [];
    if (org != null) constraints.push(where('organizationId', '==', org));
    if (processId) constraints.push(where('processId', '==', processId));
    constraints.push(where('isActive', '==', true));
    constraints.push(orderBy('createdAt', 'desc'));
    const q = query(this.collectionRef, ...constraints);
    const snapshot = await getDocs(q);
    let items = snapshot.docs.slice(0, limit).map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        lastMeasuredAt: data.lastMeasuredAt?.toDate?.() ?? data.lastMeasuredAt,
        belowTarget: this.isBelowTarget(data)
      };
    });
    if (belowTargetOnly) items = items.filter((m) => m.belowTarget);
    return items;
  }

  async getBelowTarget(organizationId) {
    return this.getByOrganization(organizationId, { belowTargetOnly: true });
  }

  async update(metricId, updates, userId) {
    const ref = doc(this.collectionRef, metricId);
    const patch = omitUndefined({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    if (updates.lastMeasuredAt) patch.lastMeasuredAt = Timestamp.fromDate(new Date(updates.lastMeasuredAt));
    await updateDoc(ref, patch);
    try {
      auditService.logUpdate('performance_metrics', metricId, updates.name || metricId, {
        changedFields: Object.keys(updates)
      });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }

  async delete(metricId) {
    const ref = doc(this.collectionRef, metricId);
    const current = await this.get(metricId);
    await deleteDoc(ref);
    try {
      auditService.logDelete('performance_metrics', metricId, current?.name || metricId, {});
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }
}

export const performanceMetricService = new PerformanceMetricService();
export { METRIC_TYPES, UNITS, FREQUENCIES, TRENDS, DATA_SOURCES };
